import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { createCheckoutSession } from "../api/subscriptions";
import { useSubmissionLock } from "../hooks/useSubmissionLock";

const CHECKOUT_PLANS = {
  PLUS: {
    key: "PLUS",
    label: "Beem Plus",
    monthlyPrice: "Only £29 /month",
    yearlyPrice: "£245 /year (Save 15%)",
    description:
      "Unlock up to 5 properties, increased contact allowance, analytics and payments.",
    features: [
      "Up to 5 properties",
      "Increased contact allowance",
      "Payments",
      "Advanced analytics",
      "Cancel at any time",
    ],
  },
  ENHANCED: {
    key: "ENHANCED",
    label: "Beem Enhanced",
    monthlyPrice: "£69 /month",
    yearlyPrice: "Yearly billing available",
    description:
      "Unlock tenant portal, maintenance jobs, scheduling, quotes and proof of work.",
    features: [
      "Everything in Plus",
      "Up to 20 properties",
      "Tenant portal",
      "Maintenance jobs",
      "Quotes, scheduling and proof of work",
    ],
  },
};

function normalizeCheckoutPlan(plan) {
  return String(plan || "").toUpperCase().includes("ENHANCED")
    ? CHECKOUT_PLANS.ENHANCED
    : CHECKOUT_PLANS.PLUS;
}

export default function CheckoutDrawer({ open, onClose, plan = "PLUS" }) {
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { run: runSubmission, isPending } = useSubmissionLock();

  const checkoutPlan = useMemo(() => normalizeCheckoutPlan(plan), [plan]);
  const pending = loading || isPending("checkout");

  useEffect(() => {
    if (!open) return;
    setError("");
    setLoading(false);
  }, [open, checkoutPlan.key]);

  const handleCheckout = async () => {
    if (pending) return;

    await runSubmission("checkout", async () => {
      try {
        setError("");
        setLoading(true);

        const response = await createCheckoutSession({
          billingCycle,
          plan: checkoutPlan.key,
        });
        const url = response?.data?.url;

        if (!url) throw new Error("No checkout URL returned");

        onClose?.();
        window.location.href = url;
      } catch (err) {
        const status = err?.response?.status;
        const body = err?.response?.data;
        console.error("Failed to start Stripe checkout", {
          status,
          body,
          error: err,
        });

        if (status === 401) {
          setError("Your session has expired. Please log in again.");
        } else if (status === 403) {
          setError(body?.error || "Your account cannot start this checkout.");
        } else {
          setError(
            body?.detail ||
              body?.error ||
              "Could not open checkout. Please try again."
          );
        }
        setLoading(false);
      }
    });
  };

  const price =
    billingCycle === "monthly"
      ? checkoutPlan.monthlyPrice
      : checkoutPlan.yearlyPrice;

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          <motion.div
            className="fixed right-0 top-0 h-full w-full sm:w-[480px] bg-[#1c1c1c] border-l border-white/10 text-white z-50 flex flex-col shadow-2xl"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 70, damping: 20 }}
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-white/10 bg-[#232323]">
              <h2 className="text-xl font-semibold">
                Upgrade to {checkoutPlan.label}
              </h2>
              <button
                type="button"
                onClick={onClose}
                className="p-2 rounded-full hover:bg-white/10 transition"
                aria-label="Close checkout"
              >
                <X size={22} />
              </button>
            </div>

            <div className="flex justify-center mt-8">
              <div className="flex items-center bg-white/10 rounded-full p-1">
                <button
                  type="button"
                  onClick={() => setBillingCycle("monthly")}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                    billingCycle === "monthly"
                      ? "bg-white text-black shadow-md"
                      : "text-white/70 hover:text-white"
                  }`}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  onClick={() => setBillingCycle("yearly")}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                    billingCycle === "yearly"
                      ? "bg-white text-black shadow-md"
                      : "text-white/70 hover:text-white"
                  }`}
                >
                  Yearly
                </button>
              </div>
            </div>

            <div className="flex-1 flex flex-col px-8 py-10">
              <div className="text-center">
                <h3 className="text-3xl font-bold text-white mb-2">
                  {loading ? "Opening secure checkout..." : price}
                </h3>
                <p className="text-white/70 text-sm leading-relaxed max-w-xs mx-auto">
                  {checkoutPlan.description}
                </p>
              </div>

              <ul className="mt-10 text-left text-white/80 text-sm space-y-3 max-w-sm mx-auto w-full">
                {checkoutPlan.features.map((feature) => (
                  <li key={feature} className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-[#AF9B8A] rounded-full" />
                    {feature}
                  </li>
                ))}
              </ul>

              <div className="mt-auto pt-10">
                <p className="text-sm text-white/60 mb-4 text-center">
                  You&apos;ll be taken to a secure Stripe checkout page to
                  complete your {billingCycle} {checkoutPlan.label} subscription.
                </p>

                {error && (
                  <p className="text-xs text-red-400 mb-3 text-center">
                    {error}
                  </p>
                )}

                <button
                  type="button"
                  onClick={handleCheckout}
                  disabled={pending}
                  className="w-full py-4 bg-white text-black font-semibold rounded-2xl hover:bg-[#AF9B8A]/90 hover:text-white transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                  aria-busy={pending}
                >
                  {pending ? "Opening Checkout..." : "Proceed to Payment"}
                </button>
              </div>
            </div>

            <div className="py-4 text-center text-white/60 text-xs">
              Secure payments processed by Stripe
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
